import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Input, Dropdown, Checkbox, Icon } from 'semantic-ui-react'
import { QRCodeCanvas } from 'qrcode.react'

import { HUMAN_GAME } from 'types/deck'

import { useAppSelector } from 'redux/hooks'

import Modal from 'components/elements/Modal'

import { humanReadableObjectToDropdown } from 'utils/DropdownConversions'

import styles from './shareModal.module.scss'

type Props = { open: boolean; onClose: () => void; collectionIsPublic: boolean }

const ShareModal = ({ open, onClose, collectionIsPublic }: Props) => {
  const router = useRouter()

  const pageGame = Number(router.query.collectionGame) || 1

  const tagMap = useAppSelector(state => state.collection.tagMap) // TODO - This needs to be migrated to collectionV2

  const [game, setGame] = useState(pageGame)
  const [tags, setTags] = useState<number[]>([])
  const [isCurrentSearch, setIsCurrentSearch] = useState(false)
  const [shareLink, setShareLink] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const link = isCurrentSearch
      ? `${window.origin}${location.pathname}${location.search}`
      : `${window.origin}${location.pathname}?collectionGame=${game}${tags.length ? `&tags=${tags.join(',')}` : ``}`

    setShareLink(link)
  }, [router, isCurrentSearch, game, tags])

  const handleCopy = () => {
    navigator.clipboard.writeText(shareLink)

    setCopied(true)

    setTimeout(() => setCopied(false), 3000) // 3 seconds
  }

  return (
    <Modal closeButton open={open} onClose={onClose} modalClassName={styles.modal}>
      <h2>Share your collection</h2>

      {collectionIsPublic ? (
        <div className={styles.shareContainer}>
          <div className={styles.inputContainer}>
            <Input
              size="large"
              iconPosition="left"
              labelPosition="right"
              action={{
                color: copied ? 'green' : 'blue',
                onClick: handleCopy,
                content: copied ? 'Copied' : 'Copy',
              }}
              icon="linkify"
              value={shareLink}
            />
          </div>

          <div className={styles.optionsContainer}>
            <Dropdown
              disabled={isCurrentSearch}
              selection
              name="game"
              options={humanReadableObjectToDropdown(HUMAN_GAME)}
              onChange={(_e, { value }) => setGame(value as number)}
              value={game}
            />
            <Dropdown
              disabled={isCurrentSearch}
              multiple
              selection
              search
              name="tags"
              placeholder="Filter by tags..."
              options={Object.values(tagMap).map(tag => ({ value: tag.id, text: tag.tagname }))}
              onChange={(_e, { value }) => setTags(value as number[])}
              value={tags}
            />
            <Checkbox
              checked={isCurrentSearch}
              label="Use current collection page (any searches you have applied will be linked)"
              onChange={() => setIsCurrentSearch(!isCurrentSearch)}
            />
          </div>

          <QRCodeCanvas size={256} value={shareLink} />
        </div>
      ) : (
        <div className={styles.publicMessage}>
          <p>Your collection must be made public in order to share it. </p>
          <p>
            You can set your collection to public in the{' '}
            <b>
              <Icon name="cog" /> Collection Options
            </b>
          </p>
        </div>
      )}
    </Modal>
  )
}

export default ShareModal
